import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Mic, MicOff, Loader2, Languages } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: '', label: 'Auto-detect' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'pl', label: 'Polish' },
  { code: 'ro', label: 'Romanian' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ur', label: 'Urdu' },
  { code: 'ar', label: 'Arabic' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'tl', label: 'Filipino' },
  { code: 'sw', label: 'Swahili' },
  { code: 'yo', label: 'Yoruba' },
  { code: 'ha', label: 'Hausa' },
];

export function SpeechToTextButton({ onTranscript, onTranslation, compact = false }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [translateTo, setTranslateTo] = useState('');
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: 'audio/webm' });

        if (blob.size < 100) {
          toast.error('Recording too short');
          return;
        }

        setProcessing(true);
        try {
          // Transcribe
          const formData = new FormData();
          formData.append('file', blob, 'recording.webm');

          const res = await fetch(`${API_URL}/api/speech/transcribe`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
            body: formData,
          });

          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.detail || 'Transcription failed');
          }

          const data = await res.json();
          if (data.text) {
            onTranscript(data.text);
            toast.success(`Transcribed (${data.language || 'detected'})`);

            // Translate if requested
            if (translateTo && translateTo !== data.language) {
              const targetLang = LANGUAGES.find(l => l.code === translateTo)?.label || translateTo;
              try {
                const tRes = await fetch(`${API_URL}/api/speech/translate`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                  },
                  body: JSON.stringify({ text: data.text, target_language: targetLang }),
                });
                if (tRes.ok) {
                  const tData = await tRes.json();
                  if (onTranslation) {
                    onTranslation(tData.translated_text);
                  } else {
                    onTranscript(tData.translated_text);
                  }
                  toast.success(`Translated to ${targetLang}`);
                }
              } catch (err) {
                console.error('Translation error:', err);
              }
            }
          } else {
            toast.error('No speech detected');
          }
        } catch (err) {
          toast.error(err.message || 'Transcription failed');
        } finally {
          setProcessing(false);
        }
      };

      mediaRecorder.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
    setRecording(false);
  };

  if (processing) {
    return (
      <Button type="button" disabled size={compact ? 'icon' : 'sm'}
        className="bg-amber-500/10 text-amber-400 border border-amber-500/30" data-testid="stt-processing">
        <Loader2 className={`${compact ? 'w-4 h-4' : 'w-4 h-4 mr-1.5'} animate-spin`} />
        {!compact && 'Processing...'}
      </Button>
    );
  }

  return (
    <div className="relative inline-flex items-center gap-1">
      <Button
        type="button"
        size={compact ? 'icon' : 'sm'}
        onClick={recording ? stopRecording : startRecording}
        className={recording
          ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
          : 'bg-[#00A6A0]/10 hover:bg-[#00A6A0]/20 text-[#00A6A0] border border-[#00A6A0]/30'
        }
        data-testid="stt-btn"
      >
        {recording ? (
          <><MicOff className={`${compact ? 'w-4 h-4' : 'w-4 h-4 mr-1.5'}`} />{!compact && 'Stop'}</>
        ) : (
          <><Mic className={`${compact ? 'w-4 h-4' : 'w-4 h-4 mr-1.5'}`} />{!compact && 'Speak'}</>
        )}
      </Button>

      {/* Translate toggle */}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={() => setShowLangMenu(!showLangMenu)}
        className={`h-8 w-8 ${translateTo ? 'text-[#FF6F61]' : 'text-muted-foreground'}`}
        data-testid="stt-translate-btn"
        title={translateTo ? `Translating to ${LANGUAGES.find(l => l.code === translateTo)?.label}` : 'Enable translation'}
      >
        <Languages className="w-4 h-4" />
      </Button>

      {showLangMenu && (
        <div className="absolute bottom-full right-0 mb-1 z-50 bg-[#1A2329] border border-border rounded-xl shadow-xl p-2 min-w-[160px] max-h-48 overflow-y-auto"
          data-testid="stt-lang-menu">
          <p className="text-[10px] text-muted-foreground px-2 py-1 font-medium">Translate to:</p>
          <button onClick={() => { setTranslateTo(''); setShowLangMenu(false); }}
            className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-muted/50 ${!translateTo ? 'text-[#00A6A0] font-medium' : 'text-foreground'}`}>
            Off (no translation)
          </button>
          {LANGUAGES.filter(l => l.code).map(l => (
            <button key={l.code} onClick={() => { setTranslateTo(l.code); setShowLangMenu(false); toast.info(`Will translate to ${l.label}`); }}
              className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-muted/50 ${translateTo === l.code ? 'text-[#FF6F61] font-medium' : 'text-foreground'}`}>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
